import { AVAILABLE_LOCALES, DEFAULT_LOCALE } from "@/lib/i18n/client"
import type { Locale } from "@/types"

/**
 * OKS-21 — Locale resolver for the **public** invite landing page.
 *
 * The landing route `/i/:token` is unauthenticated — no
 * `currentUser.locale` shared prop is present. Resolution chain
 * (OKS-536 — `brand.locale` promoted above `navigator.language`):
 *
 *   1. Explicit locale query param — `?locale=` (the shipped OKS-24
 *      invite-landing switch) OR `?lang=` (UX Spec §9). Either lets a
 *      brand admin force a locale when sharing, and outranks everything
 *      including `brand.locale` (an explicit human request beats the
 *      brand default). `?locale=` is read first, then `?lang=`; both are
 *      sanitized against `AVAILABLE_LOCALES`. This is a client-only
 *      affordance (the server renders `brand.locale` so crawler OG
 *      unfurls stay on the brand's canonical language — TRD note in
 *      `spec/requests/public/invite_landings_locale_spec.rb`).
 *   2. `brand.locale` from the Inertia prop — the inviting brand's
 *      default, backend-supplied. This is a brand-presenting-itself
 *      surface: the candidate has no locale preference yet, so the
 *      brand's language wins over the visitor's browser (OKS-536 /
 *      PRD §3). Matches the server-side brand-locale render, so the
 *      first paint and post-hydration client render agree — no locale
 *      flicker on a `fr` brand opened in an `en` browser.
 *   3. `navigator.language` — last-ditch fallback, only reached when
 *      `brand.locale` is somehow invalid/unavailable. Truncated to the
 *      base tag ("fr-CA" → "fr") and matched against `AVAILABLE_LOCALES`.
 *   4. `DEFAULT_LOCALE` (`"fr"`) — final fallback per PRD §6.
 *
 * No `localStorage` / `sessionStorage` — Crew project rule (Inertia
 * flash/session only).
 */

/** Parse an explicit locale override from a URL search string. Reads
 *  `?locale=` first (the shipped OKS-24 invite-landing switch), then
 *  `?lang=` (UX Spec §9). Returns `null` if neither is present or the
 *  value isn't a recognised Crew locale. */
function readLocaleQueryParam(search: string): Locale | null {
  if (!search) return null
  let params: URLSearchParams
  try {
    params = new URLSearchParams(search)
  } catch {
    // Defensive — invalid URL fragments shouldn't crash landing page.
    return null
  }
  const value = params.get("locale") ?? params.get("lang")
  if (value === null) return null
  return AVAILABLE_LOCALES.includes(value as Locale) ? (value as Locale) : null
}

/** Truncate a BCP-47 tag (`fr-CA`) to its primary subtag (`fr`) and
 *  match against the Crew locale set. Returns `null` if no match. */
function readNavigatorLocale(
  language: string | undefined | null,
): Locale | null {
  if (!language) return null
  const base = language.toLowerCase().split("-")[0]
  return AVAILABLE_LOCALES.includes(base as Locale) ? (base as Locale) : null
}

export interface ResolvePublicLocaleArgs {
  /** Brand's default locale from the Inertia prop. Backend-supplied. */
  brandLocale: Locale
}

/**
 * Resolve the locale for a public landing page render. Pure function
 * — easy to unit-test. Reads the live `window.location.search` and
 * `navigator.language` globals directly; callers pass only `brandLocale`
 * (OKS-784 — the prior `search`/`navigatorLanguage` params existed solely
 * as a test-only override and are gone; specs now drive the real globals
 * via the `@/test/test-utils` URL harness and `navigator.language`
 * mocking instead).
 */
export function resolvePublicLocale({
  brandLocale,
}: ResolvePublicLocaleArgs): Locale {
  const search = typeof window !== "undefined" ? window.location.search : ""
  const navigatorLanguage =
    typeof navigator !== "undefined" ? navigator.language : null

  return (
    readLocaleQueryParam(search) ??
    (AVAILABLE_LOCALES.includes(brandLocale) ? brandLocale : null) ??
    readNavigatorLocale(navigatorLanguage) ??
    DEFAULT_LOCALE
  )
}
